import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getIndustries, getCategories, uploadDocument, getDocumentMetadata } from '../../services/industry-insights';
import AdminRoute from '../../components/AdminRoute';

interface FormData {
  industry: string;
  category: string;
  title: string;
  description: string;
  tags: string;
  file: File | null;
  content: string;
}

const NoteFormPage: React.FC = () => {
  const { industry, category, filename } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = !!filename;
  
  // Get any state passed from the list view
  const locationState = location.state as { industry?: string; category?: string } | null;

  // Form state
  const [formData, setFormData] = useState<FormData>({
    industry: industry || locationState?.industry || '',
    category: category || locationState?.category || '',
    title: '',
    description: '',
    tags: '',
    file: null,
    content: ''
  });

  // UI state
  const [industries, setIndustries] = useState<{ name: string; display_name: string }[]>([]);
  const [categories, setCategories] = useState<{ name: string; display_name: string }[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<boolean>(false);
  const [fileError, setFileError] = useState<string | null>(null);

  // Load industries on component mount
  useEffect(() => {
    const loadIndustries = async () => {
      try {
        setLoading(true);
        const response = await getIndustries();
        setIndustries(response.industries);
        setLoading(false);
      } catch (err) {
        setError('Failed to load industries');
        setLoading(false);
        console.error(err);
      }
    };

    loadIndustries();
  }, []);

  // Load categories when an industry is selected
  useEffect(() => {
    if (!formData.industry) {
      setCategories([]);
      return;
    }

    const loadCategories = async () => {
      try {
        setLoading(true);
        const response = await getCategories(formData.industry);
        setCategories(response.categories);
        setLoading(false);
      } catch (err) {
        setError('Failed to load categories');
        setLoading(false);
        console.error(err);
      }
    };

    loadCategories();
  }, [formData.industry]);

  // Load note data in edit mode
  useEffect(() => {
    if (!isEditMode || !industry || !category || !filename) return;

    const loadNoteData = async () => {
      try {
        setLoading(true);
        const document = await getDocumentMetadata(industry, category, filename);

        // Fetch the document content
        const contentResponse = await fetch(document.url);
        const content = await contentResponse.text();

        setFormData({
          industry,
          category,
          title: document.metadata?.title || '',
          description: document.metadata?.description || '',
          tags: document.metadata?.tags?.join(', ') || '',
          file: null, // We don't need to set the file in edit mode
          content
        });

        setLoading(false);
      } catch (err) {
        setError('Failed to load note data');
        setLoading(false);
        console.error(err);
      }
    };

    loadNoteData();
  }, [isEditMode, industry, category, filename]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Basic validation
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setFileError('File size exceeds 10MB limit');
        return;
      }
      
      // Clear any previous errors
      setFileError(null);
      
      setFormData(prevData => ({
        ...prevData,
        file
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.industry || !formData.category || !formData.title) {
      setError('Industry, category, and title are required');
      return;
    }
    
    // In edit mode, we don't need to upload a file if none is selected
    if (!isEditMode && !formData.file && !formData.content) {
      setError('Please provide either a file or content');
      return;
    }

    try {
      setLoading(true);
      
      // Prepare tags array
      const tags = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
      
      let uploadedFile: File | null = formData.file;

      // If we have content but no file, create a file from the content
      if (!uploadedFile && formData.content) {
        const contentBlob = new Blob([formData.content], { type: 'text/markdown' });
        uploadedFile = new File([contentBlob], `${formData.title.toLowerCase().replace(/\s+/g, '-')}.md`, { type: 'text/markdown' });
      }

      if (!uploadedFile) {
        throw new Error('No file to upload');
      }

      // Prepare form data for upload
      const uploadFormData = new FormData();
      uploadFormData.append('industry', formData.industry);
      uploadFormData.append('category', formData.category);
      uploadFormData.append('file', uploadedFile);
      uploadFormData.append('title', formData.title);
      
      if (formData.description) {
        uploadFormData.append('description', formData.description);
      }
      
      if (tags.length > 0) {
        uploadFormData.append('tags', tags.join(','));
      }
      
      // Add an overwrite flag for edit mode
      if (isEditMode) {
        uploadFormData.append('overwrite', 'true');
        uploadFormData.append('original_filename', filename);
      }

      await uploadDocument(uploadFormData);

      // Navigate back to the list view
      navigate('/admin/industry-insights');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save note');
      setLoading(false);
      console.error(err);
    }
  };

  const togglePreview = () => {
    setPreviewMode(!previewMode);
  };

  return (
    <AdminRoute>
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">
            {isEditMode ? 'Edit Note' : 'Add New Note'}
          </h1>
          <button
            onClick={() => navigate('/admin/industry-insights')}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded"
          >
            Cancel
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" role="status">
              <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">Loading...</span>
            </div>
            <p className="mt-2 text-gray-600">Loading...</p>
          </div>
        ) : error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Error:</strong>
            <span className="block sm:inline"> {error}</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-1">
                  Industry <span className="text-red-500">*</span>
                </label>
                <select
                  id="industry"
                  name="industry"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.industry}
                  onChange={handleInputChange}
                  disabled={isEditMode}
                  required
                >
                  <option value="">Select an industry</option>
                  {industries.map((industry) => (
                    <option key={industry.name} value={industry.name}>
                      {industry.display_name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  id="category"
                  name="category"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.category}
                  onChange={handleInputChange}
                  disabled={!formData.industry || isEditMode}
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category.name} value={category.name}>
                      {category.display_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Enter a descriptive title"
              />
            </div>

            <div className="mb-6">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter a brief description"
              />
            </div>

            <div className="mb-6">
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                Tags (comma separated)
              </label>
              <input
                type="text"
                id="tags"
                name="tags"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.tags}
                onChange={handleInputChange}
                placeholder="tag1, tag2, tag3"
              />
              <p className="text-sm text-gray-500 mt-1">Separate tags with commas</p>
            </div>

            {!isEditMode && (
              <div className="mb-6">
                <label htmlFor="file" className="block text-sm font-medium text-gray-700 mb-1">
                  Upload File (Optional)
                </label>
                <input
                  type="file"
                  id="file"
                  name="file"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onChange={handleFileChange}
                />
                {fileError && (
                  <p className="text-sm text-red-500 mt-1">{fileError}</p>
                )}
                <p className="text-sm text-gray-500 mt-1">
                  Supported file types: PDF, DOCX, Markdown, and text files. Max size: 10MB.
                </p>
              </div>
            )}

            <div className="mb-6">
              <div className="flex justify-between items-center mb-1">
                <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                  Content {!isEditMode && !formData.file && <span className="text-red-500">*</span>}
                </label>
                <button
                  type="button"
                  onClick={togglePreview}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {previewMode ? 'Edit' : 'Preview'}
                </button>
              </div>
              {previewMode ? (
                <div 
                  className="w-full border border-gray-300 rounded-md px-4 py-3 prose max-w-none min-h-[300px] bg-gray-50"
                  dangerouslySetInnerHTML={{ __html: formData.content ? marked(formData.content) : '<p class="text-gray-400">No content to preview</p>' }}
                />
              ) : (
                <textarea
                  id="content"
                  name="content"
                  rows={12}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                  value={formData.content}
                  onChange={handleInputChange}
                  placeholder="# Markdown Content
                  
Enter your note content here. You can use Markdown formatting:

## Headers
**Bold text**
*Italic text*
- Bullet points
1. Numbered lists

> Blockquotes

[Links](https://example.com)

```
Code blocks
```

And more..."
                />
              )}
              <p className="text-sm text-gray-500 mt-1">
                Supports Markdown formatting
              </p>
            </div>

            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => navigate('/admin/industry-insights')}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
                disabled={loading}
              >
                {loading ? 'Saving...' : isEditMode ? 'Update Note' : 'Save Note'}
              </button>
            </div>
          </form>
        )}
      </div>
    </AdminRoute>
  );
};

// Simple marked implementation for preview
function marked(markdown: string): string {
  // This is a very basic implementation - in a real app, use a proper library
  const html = markdown
    // Headers
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    // Bold and italic
    .replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
    .replace(/\*(.*)\*/gim, '<em>$1</em>')
    // Lists
    .replace(/^- (.*$)/gim, '<ul><li>$1</li></ul>')
    .replace(/^\d\. (.*$)/gim, '<ol><li>$1</li></ol>')
    // Links
    .replace(/\[(.+?)\]\((.+?)\)/gim, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    // Code blocks
    .replace(/```([\s\S]*?)```/gim, '<pre><code>$1</code></pre>')
    // Blockquotes
    .replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>')
    // Line breaks
    .replace(/\n/gim, '<br>');
  
  return html;
}

export default NoteFormPage;
